import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import ListItem from './components/ListItem'
import PlusCircleIcon from '../../../../assets/icon/PlusCircleIcon'
import CheckIcon from '../../../../assets/icon/CheckIcon'
import CalendarSmallIcon from '../../../../assets/icon/CalendarSmallIcon'
import StarIcon from '../../../../assets/icon/StarIcon'
import UsersIcon from '../../../../assets/icon/UsersIcon'
import FolderIcon from '../../../../assets/icon/FolderIcon'
import TimeIcon from '../../../../assets/icon/TimeIcon'
import CreatedDateIcon from '../../../../assets/icon/CreatedDateIcon'
import CreatorIcon from '../../../../assets/icon/CreatorIcon'
import TaskTypeTaskIcon from '../../../../assets/icon/TaskTypeTaskIcon'
import ProjectStatusesIcon from '../../../../assets/icon/ProjectStatusesIcon'
import DropDown from '../../../../components/DropDown'
import { Task } from "../../../../types/task";
import useTaskDragAndDrop from '../../hooks/useTaskDragAndDrop';
import TaskService from '../../../../services/task.service';
import { getWheelDeltaPx, scrollHorizontallyByWheel } from '../../../../utils/horizontalWheel';
import {
    LIST_VIEW_COLUMN_ORDER,
    ListViewColumnKey,
    readProjectListViewColumns,
    writeProjectListViewColumns,
} from '../../utils/listViewColumns';

export default function ListView({ tasks, allTasks = tasks, project, projectUsers = [], getTasks, onTaskUpdated, onTaskOpen, onTasksReordered, canPersistOrder = true, columnsStorageId, includeChildTasks = true }: any) {
    const visibleTasks = useMemo(() => (
        includeChildTasks
            ? (tasks || [])
            : (tasks || []).filter((task: Task) => !task.parent_id)
    ), [includeChildTasks, tasks])
    const parentTitles = useMemo(() => new Map<string, string>((allTasks || []).map((task: Task): [string, string] => [String(task.id), task.title])), [allTasks])
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const listViewStorageId = columnsStorageId ?? project?.id
    const [visibleColumns, setVisibleColumns] = useState(() => readProjectListViewColumns(listViewStorageId))
    const effectiveVisibleColumns = visibleColumns
    const persistTaskOrder = useCallback(async ({ nextTasks }: { nextTasks: Task[] }) => {
        if (!canPersistOrder) {
            return
        }

        const statusesToPersist = Array.from(new Set(
            nextTasks
                .map((task) => task.status)
                .filter((status): status is string => Boolean(status))
        ))

        await Promise.all(statusesToPersist.map(async (status) => {
            const taskIds = nextTasks
                .filter((task) => task.status === status)
                .map((task) => task.id)

            await TaskService.reorder({
                project_id: project.id,
                status,
                task_ids: taskIds,
            })
        }))
    }, [canPersistOrder, project.id])
    const {
        beginDrag,
        commitDrop,
        draggedTaskId,
        dropTarget,
        endDrag,
        isDragging,
        orderedTasks,
        pendingTaskId,
        previewMove,
    } = useTaskDragAndDrop<Task>({
        tasks: visibleTasks,
        onTasksCommitted: onTasksReordered,
        onOrderPersist: persistTaskOrder,
    })

    const columnOptions = useMemo(() => ([
        { key: 'title' as ListViewColumnKey, label: 'Название задачи', icon: <span className={styles.listView__menuIconText}>Aa</span> },
        { key: 'dueDate' as ListViewColumnKey, label: 'Сроки', icon: <CalendarSmallIcon /> },
        { key: 'priority' as ListViewColumnKey, label: 'Приоритет', icon: <StarIcon /> },
        { key: 'status' as ListViewColumnKey, label: 'Статус', icon: <ProjectStatusesIcon /> },
        { key: 'users' as ListViewColumnKey, label: 'Участники', icon: <UsersIcon color="#64617E" /> },
        { key: 'project' as ListViewColumnKey, label: 'Проект', icon: <FolderIcon /> },
        { key: 'taskType' as ListViewColumnKey, label: 'Тип задачи', icon: <TaskTypeTaskIcon /> },
        { key: 'createdAt' as ListViewColumnKey, label: 'Дата создания', icon: <CreatedDateIcon /> },
        { key: 'creator' as ListViewColumnKey, label: 'Создатель', icon: <CreatorIcon /> },
        { key: 'timeTracking' as ListViewColumnKey, label: 'Учет времени', icon: <TimeIcon color="#64617E" size={16} /> },
        { key: 'totalTimeSpent' as ListViewColumnKey, label: 'Общее время', icon: <TimeIcon color="#64617E" size={16} /> },
    ]), [])
    const isAllSelected = selectedIds.length === orderedTasks.length
    const isSomeSelected = selectedIds.length > 0 && !isAllSelected
    const visibleColumnsCount = [
        effectiveVisibleColumns.dueDate,
        effectiveVisibleColumns.priority,
        effectiveVisibleColumns.status,
        effectiveVisibleColumns.users,
        effectiveVisibleColumns.project,
        effectiveVisibleColumns.taskType,
        effectiveVisibleColumns.createdAt,
        effectiveVisibleColumns.creator,
        effectiveVisibleColumns.timeTracking,
        effectiveVisibleColumns.totalTimeSpent,
    ].filter(Boolean).length
    useEffect(() => {
        setVisibleColumns(readProjectListViewColumns(listViewStorageId))
    }, [listViewStorageId])

    useEffect(() => {
        if (listViewStorageId == null) {
            return
        }

        writeProjectListViewColumns(listViewStorageId, effectiveVisibleColumns)
    }, [listViewStorageId, effectiveVisibleColumns])

    // Horizontal scroll area for the middle columns. The first column
    // (checkbox + title) and the actions column are pinned via sticky CSS;
    // the rest scrolls with the mouse wheel. `edges` drives the fade masks.
    const scrollRef = useRef<HTMLDivElement>(null)
    const viewportRef = useRef<HTMLDivElement>(null)
    const [edges, setEdges] = useState({ left: false, right: false })

    const updateEdges = useCallback(() => {
        const node = scrollRef.current
        if (!node) return
        const maxScroll = node.scrollWidth - node.clientWidth
        const left = node.scrollLeft > 1
        const right = node.scrollLeft < maxScroll - 1
        setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
    }, [])

    useEffect(() => {
        const node = scrollRef.current
        const viewport = viewportRef.current
        if (!node || !viewport) return

        // Translate the wheel into horizontal scroll while there's hidden
        // content; otherwise let the page scroll vertically as usual.
        const handleWheel = (event: WheelEvent) => {
            // honour both axes (trackpads send deltaX)
            const raw = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX
            if (raw === 0) return

            scrollHorizontallyByWheel(node, getWheelDeltaPx(raw, event, node), event)
        }

        viewport.addEventListener('wheel', handleWheel, { capture: true, passive: false })
        node.addEventListener('scroll', updateEdges, { passive: true })
        return () => {
            viewport.removeEventListener('wheel', handleWheel, { capture: true })
            node.removeEventListener('scroll', updateEdges)
        }
    }, [updateEdges])

    useEffect(() => {
        updateEdges()
        const node = scrollRef.current
        if (!node || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(updateEdges)
        observer.observe(node)
        return () => observer.disconnect()
    }, [updateEdges, visibleColumnsCount, orderedTasks.length])

    const handleSelectAll = () => {
        if (selectedIds.length > 0) {
            setSelectedIds([])
        } else {
            setSelectedIds(orderedTasks.map((t: Task) => t.id))
        }
    }

    const handleToggleItem = (id: number) => {
        setSelectedIds((prev) => (
            prev.includes(id)
                ? prev.filter((currentId) => currentId !== id)
                : [...prev, id]
        ))
    }

    const toggleColumn = (key: ListViewColumnKey) => {
        if (key === 'title') {
            return
        }

        setVisibleColumns((prev) => ({
            ...prev,
            [key]: !prev[key],
        }))
    }

    return (
        <div
            ref={viewportRef}
            className={styles.listViewViewport}
            style={{
                ['--list-view-visible-columns' as any]: visibleColumnsCount,
                ['--list-view-status-extra-width' as any]: '0px',
            }}
        >
            {edges.left && (
                <>
                    <div className={`${styles.listViewFade} ${styles.listViewFade_left} ${styles.listViewFade_header}`} aria-hidden="true" />
                    <div className={`${styles.listViewFade} ${styles.listViewFade_left} ${styles.listViewFade_body}`} aria-hidden="true" />
                </>
            )}
            {edges.right && (
                <>
                    <div className={`${styles.listViewFade} ${styles.listViewFade_right} ${styles.listViewFade_header}`} aria-hidden="true" />
                    <div className={`${styles.listViewFade} ${styles.listViewFade_right} ${styles.listViewFade_body}`} aria-hidden="true" />
                </>
            )}
            <div ref={scrollRef} className={styles.listView}>
            <div className={styles.listView__header}>
                <div className={styles.listView__headerCheckbox}>
                    <div
                        className={`${styles.listView__checkbox} ${isAllSelected ? styles.listView__checkboxActive : ''} ${isSomeSelected ? styles.listView__checkboxPartial : ''}`}
                        onClick={handleSelectAll}
                    >
                        {isAllSelected && <CheckIcon />}
                        {isSomeSelected && <div className={styles.listView__checkboxDash} />}
                    </div>
                </div>
                <div className={styles.listView__headerTitle}>Название задачи</div>
                {effectiveVisibleColumns.dueDate && <div className={styles.listView__headerDate}>Сроки</div>}
                {effectiveVisibleColumns.priority && <div className={styles.listView__headerPriority}>Приоритет</div>}
                {effectiveVisibleColumns.status && <div className={styles.listView__headerStatus}>Статус</div>}
                {effectiveVisibleColumns.users && <div className={styles.listView__headerUsers}>Участники</div>}
                {effectiveVisibleColumns.project && <div className={styles.listView__headerProject}>Проект</div>}
                {effectiveVisibleColumns.taskType && <div className={styles.listView__headerTaskType}>Тип задачи</div>}
                {effectiveVisibleColumns.createdAt && <div className={styles.listView__headerCreatedAt}>Дата создания</div>}
                {effectiveVisibleColumns.creator && <div className={styles.listView__headerCreator}>Создатель</div>}
                {effectiveVisibleColumns.timeTracking && (
                    <div className={`${styles.listView__headerTimeTracking} ${!effectiveVisibleColumns.totalTimeSpent ? styles.listView__headerLastColumn : ''}`}>
                        Учет времени
                    </div>
                )}
                {effectiveVisibleColumns.totalTimeSpent && <div className={styles.listView__headerTotalTimeSpent}>Общее время</div>}
                <div className={styles.listView__headerActions}>
                    <DropDown
                        position="right"
                        usePortal
                        btn={(
                            <button type="button" className={styles.listView__headerAdd}>
                                <PlusCircleIcon />
                            </button>
                        )}
                    >
                        <div className={styles.listView__menu}>
                            {LIST_VIEW_COLUMN_ORDER.map((key) => {
                                const option = columnOptions.find((columnOption) => columnOption.key === key)

                                if (!option) {
                                    return null
                                }

                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        className={`${styles.listView__menuItem} ${option.key === 'title' ? styles.listView__menuItemLocked : ''}`}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            toggleColumn(option.key)
                                        }}
                                        disabled={option.key === 'title'}
                                    >
                                        <span className={styles.listView__menuItemLeft}>
                                            <span className={`${styles.listView__menuIcon} ${(option.key === 'timeTracking' || option.key === 'totalTimeSpent') ? styles.listView__menuIconNatural : ''}`}>{option.icon}</span>
                                            <span>{option.label}</span>
                                        </span>
                                        <span className={`${styles.listView__menuSwitch} ${effectiveVisibleColumns[option.key] ? styles.listView__menuSwitchActive : ''} ${option.key === 'title' ? styles.listView__menuSwitchLocked : ''}`}>
                                            <span className={styles.listView__menuSwitchThumb} />
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </DropDown>
                </div>
            </div>
            <div
                className={styles.listView__body}
                onDragOver={(event) => {
                    if (!isDragging) {
                        return
                    }

                    event.preventDefault()
                }}
            >
                {orderedTasks.map((task: Task) => (
                    <ListItem
                        getTasks={getTasks}
                        dragPreviewPosition={dropTarget?.taskId === task.id && dropTarget.mode === 'list' ? dropTarget.position : null}
                        draggable={canPersistOrder && pendingTaskId == null}
                        isDragging={draggedTaskId === task.id}
                        isPending={pendingTaskId === task.id}
                        key={task.id}
                        onDragEnd={endDrag}
                        onDragStart={beginDrag}
                        onDrop={commitDrop}
                        onHover={previewMove}
                        onTaskUpdated={onTaskUpdated}
                        onTaskOpen={onTaskOpen}
                        project={project}
                        projectUsers={projectUsers}
                        visibleColumns={effectiveVisibleColumns}
                        task={task}
                        parentTitle={task.parent_id ? parentTitles.get(String(task.parent_id)) : undefined}
                        isSelected={selectedIds.includes(task.id)}
                        onToggle={() => handleToggleItem(task.id)}
                    />
                ))}
                {canPersistOrder && isDragging && (
                    <div
                        className={`${styles.listView__endDropZone} ${dropTarget?.mode === 'list' && !dropTarget?.taskId ? styles.listView__endDropZoneActive : ''}`}
                        onDragOver={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            previewMove({
                                mode: 'list',
                                position: 'end',
                                status: '',
                            })
                        }}
                        onDragEnter={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            previewMove({
                                mode: 'list',
                                position: 'end',
                                status: '',
                            })
                        }}
                        onDrop={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            void commitDrop({
                                mode: 'list',
                                position: 'end',
                                status: '',
                            })
                        }}
                    />
                )}
            </div>
            </div>
        </div>
    )
}
